import { useMemo, type FC } from 'react';
import { Typography } from '@material-ui/core';
import { StatusBadge } from '@openchoreo/backstage-design-system';
import { useTreeStyles } from './treeStyles';
import { getResourceTreeNodes } from './treeLayoutUtils';
import { getHealthStatusForTab, formatTimestamp } from '../utils';
import type {
  ResourceTreeData,
  HealthStatus,
  ResourceTreeNode,
} from '../types';

function getOverallHealth(
  releaseBindingData?: Record<string, unknown> | null,
): { label: string; status: HealthStatus; reason?: string; message?: string } {
  if (releaseBindingData) {
    if (typeof releaseBindingData.status === 'string') {
      const flatStatus = releaseBindingData.status;
      const reason = releaseBindingData.statusReason as string | undefined;
      const message = releaseBindingData.statusMessage as string | undefined;

      if (flatStatus === 'Ready')
        return { label: 'Healthy', status: 'Healthy', reason, message };
      if (flatStatus === 'Failed')
        return { label: 'Degraded', status: 'Degraded', reason, message };
      if (flatStatus === 'NotReady') {
        if (reason === 'ResourcesUndeployed')
          return { label: 'Undeployed', status: 'Undeployed', reason, message };
        return { label: 'Progressing', status: 'Progressing', reason, message };
      }
    } else {
      const bindingStatus = releaseBindingData.status as
        | Record<string, unknown>
        | undefined;
      const bindingConditions = Array.isArray(bindingStatus?.conditions)
        ? bindingStatus.conditions
        : [];
      const readyCondition = bindingConditions.find(
        (c: any) => c.type === 'Ready',
      );
      if (readyCondition) {
        const condStatus = (readyCondition as any).status;
        const reason = (readyCondition as any).reason as string | undefined;
        const message = (readyCondition as any).message as string | undefined;
        if (condStatus === 'True')
          return { label: 'Healthy', status: 'Healthy', reason, message };
        if (condStatus === 'False') {
          if (reason === 'ResourcesUndeployed')
            return {
              label: 'Undeployed',
              status: 'Undeployed',
              reason,
              message,
            };
          return { label: 'Degraded', status: 'Degraded', reason, message };
        }
        return { label: 'Progressing', status: 'Progressing', reason, message };
      }
    }
  }

  return { label: 'Unknown', status: 'Unknown' };
}

function getResourceCounts(nodes: ResourceTreeNode[]) {
  const counts: Partial<Record<HealthStatus, number>> = {};
  for (const n of nodes) {
    const h = n.health?.status ?? 'Unknown';
    counts[h] = (counts[h] ?? 0) + 1;
  }
  return counts;
}

function formatResourceBreakdown(
  counts: Partial<Record<HealthStatus, number>>,
): string {
  const order: HealthStatus[] = [
    'Healthy',
    'Progressing',
    'Suspended',
    'Degraded',
    'Undeployed',
    'Unknown',
  ];
  return order
    .filter(s => (counts[s] ?? 0) > 0)
    .map(s => `${counts[s]} ${s}`)
    .join(' \u00b7 ');
}

function getLatestCreatedAt(nodes: ResourceTreeNode[]): string | undefined {
  let latestEpoch: number | undefined;
  let latestIso: string | undefined;

  for (const n of nodes) {
    const createdAt = n.createdAt;
    if (!createdAt) continue;

    const date = new Date(createdAt);
    const epoch = date.getTime();
    if (Number.isNaN(epoch)) continue;

    if (latestEpoch === undefined || epoch > latestEpoch) {
      latestEpoch = epoch;
      latestIso = date.toISOString();
    }
  }

  return latestIso;
}

function formatRelativeTime(timestamp: string): string {
  if (!timestamp) return 'just now';

  const now = Date.now();
  const then = new Date(timestamp).getTime();

  if (isNaN(then)) return 'just now';

  const diffMs = now - then;

  if (diffMs <= 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

interface ReleaseStatusBarProps {
  resourceTreeData: ResourceTreeData;
  releaseBindingData?: Record<string, unknown> | null;
}

export const ReleaseStatusBar: FC<ReleaseStatusBarProps> = ({
  resourceTreeData,
  releaseBindingData,
}) => {
  const classes = useTreeStyles();

  const { health, resources, lastUpdated } = useMemo(() => {
    const h = getOverallHealth(releaseBindingData);
    const nodes = getResourceTreeNodes(resourceTreeData);
    const counts = getResourceCounts(nodes);
    const latestTime = getLatestCreatedAt(nodes);

    return {
      health: h,
      resources: {
        total: nodes.length,
        breakdown: formatResourceBreakdown(counts),
      },
      lastUpdated: latestTime
        ? {
            relative: formatRelativeTime(latestTime),
            absolute: formatTimestamp(latestTime),
          }
        : undefined,
    };
  }, [resourceTreeData, releaseBindingData]);

  const badgeStatus = getHealthStatusForTab(health.status) ?? 'default';

  return (
    <div className={classes.statusBar}>
      {/* Health Section */}
      <div className={classes.statusBarSection}>
        <Typography className={classes.statusBarLabel}>Health</Typography>
        <div className={classes.statusBarValue}>
          <StatusBadge status={badgeStatus} label={health.label} />
        </div>
        {health.reason && (
          <Typography className={classes.statusBarDetail}>
            Reason: {health.reason}
          </Typography>
        )}
        {health.message && (
          <Typography className={classes.statusBarDetail}>
            {health.message}
          </Typography>
        )}
      </div>

      {/* Resources Section */}
      <div className={classes.statusBarSection}>
        <Typography className={classes.statusBarLabel}>Resources</Typography>
        <div className={classes.statusBarValue}>
          <Typography variant="body2">
            {resources.total === 0
              ? 'No Resources'
              : `${resources.total} resource${
                  resources.total !== 1 ? 's' : ''
                }`}
          </Typography>
        </div>
        {resources.breakdown && (
          <Typography className={classes.statusBarDetail}>
            {resources.breakdown}
          </Typography>
        )}
      </div>

      {/* Last Updated Section */}
      <div className={classes.statusBarSection}>
        <Typography className={classes.statusBarLabel}>Last Updated</Typography>
        <div className={classes.statusBarValue}>
          <Typography variant="body2">
            {lastUpdated?.relative ?? 'N/A'}
          </Typography>
        </div>
        {lastUpdated?.absolute && (
          <Typography className={classes.statusBarDetail}>
            {lastUpdated.absolute}
          </Typography>
        )}
      </div>
    </div>
  );
};
